/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import MUITable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import MUITableHead from '@mui/material/TableHead';
import MUITableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import PaginationIndicator from './pagination-indicator';
import FallbackImage from './fallbacks/fallback-image';
import { ColumnDataProps } from './props/tables';
import MenuOptions, { SelectedItem, MouseEventLI } from './menus/menu-options';
import Tooltip from './info-elements/tooltip';
import Button from './button';
import FormatAPIDate from '../shared/format-api-date';
import { renderRowContentType } from './__fixtures__/table-helpers';

type Order = 'asc' | 'desc';

interface FallbackProps {
  readonly length: number;
  readonly fallback: 'empty' | 'errorCard';
}
export interface TableState {
  search: string;
  filter: string;
  sort: string;
  sortDirection: Order;
  page: number;
  perPage: number;
}

interface TableStateWithHooks extends TableState {
  handlers: {
    search: (search: string) => void;
    filter: (filter: string) => void;
    sort: (event: React.MouseEvent<unknown>, property: string) => void;
    paginate: (newPage: number) => void;
    perPageChange: (perPage: number) => void;
  }
}

export const InitialTableState: TableState = {
  search: '',
  filter: '',
  sort: '',
  sortDirection: 'asc',
  page: 1,
  perPage: 25,
};

export const useTable = (InitialState?: TableState): TableStateWithHooks => {
  const [state, setState] = useState<TableState>(InitialState ?? InitialTableState);

  const sort = (_event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = state.sort === property && state.sortDirection === 'asc';
    const sortDirection = isAsc ? 'desc' : 'asc';

    setState({ ...state, sort: property, sortDirection });
  };

  const perPageChange = (perPage: number) => {
    setState({ ...state, page: 1, perPage });
  };

  const paginate = (p: number) => setState({ ...state, page: p });
  const search = (s: string) => setState({ ...state, search: s });
  const filter = (f: string) => setState({ ...state, filter: f });

  return ({
    ...state,
    handlers: {
      search,
      filter,
      sort,
      paginate,
      perPageChange,
    },
  });
};

interface TableProps {
  readonly columns: ColumnDataProps[];
  readonly tableState: TableState;
  readonly isError: boolean;
  readonly isLoading: boolean;
  readonly data?: any;
  readonly totalItemCount?: number;
  readonly onSearch?: (search: string) => void;
  readonly onFilter?: (filter: string) => void;
  readonly onPaginate?: (newPage: number) => void;
  readonly onClick?: (rowData: any) => void;
  readonly onOptionClick?: (e: MouseEventLI, item: SelectedItem, anchor: HTMLButtonElement | null) => void;
  readonly onDeleteClick?: (event: React.MouseEvent<HTMLButtonElement>, rowData: any) => void;
  readonly onPerPageChange?: (pageChange: number) => void;
  readonly onSort?: (event: React.MouseEvent<unknown>, property: string) => void;
}

interface TableHeadProps {
  readonly order: Order;
  readonly orderBy: string;
  readonly columns: ColumnDataProps[];
  readonly numberOfRows: number;
  readonly onDeleteClick?: (event: React.MouseEvent<HTMLButtonElement>, rowData: any) => void;
  readonly onOptionClick?: (e: MouseEventLI, item: SelectedItem, anchor: HTMLButtonElement | null) => void;
  readonly onRequestSort?: (
    event: React.MouseEvent<unknown>,
    property: string
  ) => void;
}

function TableHead(props: TableHeadProps) {
  const {
    order, orderBy, onRequestSort,
    columns, onDeleteClick, onOptionClick, numberOfRows,
  } = props;

  const canSort = onRequestSort && (numberOfRows > 1);
  const shouldIncludeActionColumn = onDeleteClick || onOptionClick;

  return (
    <MUITableHead>
      <MUITableRow>
        {columns.map((column) => (
          <TableCell
            key={column.dataKey}
            style={{ width: `${column.width}%` }}
            sx={{ backgroundColor: 'background.paper' }}
            align={column.numeric ? 'right' : 'left'}
            sortDirection={orderBy === column.dataKey ? order : false}
          >
            {canSort && !column.disableSort ? (
              <TableSortLabel
                active={orderBy === column.dataKey}
                direction={orderBy === column.dataKey ? order : 'asc'}
                onClick={(e) => onRequestSort(e, column.dataKey)}
              >
                <Tooltip key={column.dataKey} isDisabled={!!column.render}>
                  <>{column.label}</>
                </Tooltip>
              </TableSortLabel>
            ) : (
              <Tooltip key={column.dataKey} isDisabled={!!column.render}>
                <>{column.label}</>
              </Tooltip>
            )}
          </TableCell>
        ))}
        {shouldIncludeActionColumn && (
          <TableCell sx={{ backgroundColor: 'background.paper', width: 60 }} />
        )}
      </MUITableRow>
    </MUITableHead>
  );
}

interface TableRowProps {
  readonly row: any;
  readonly columns: ColumnDataProps[];
  readonly onClick?: (row: any) => void;
  readonly onDeleteClick?: (event: React.MouseEvent<HTMLButtonElement>, rowData: any) => void;
  readonly onOptionClick?: (e: MouseEventLI, item: SelectedItem, anchor: HTMLButtonElement | null) => void;
}

function renderCell(row, column: ColumnDataProps) {
  if (column.date) {
    const dateOptions = {
      relativeNull: column.relativeNull ?? false,
      relativeDate: column.dateRelativeFormat ?? false,
      dateOnly: column.dateOnly ?? false,
    };
    return FormatAPIDate(row[column.dataKey], dateOptions);
  }

  return renderRowContentType(row, column);
}

function TableRow(props: TableRowProps) {
  const {
    row, columns, onClick, onDeleteClick, onOptionClick
  } = props;
  const displayMenuOptions = row.menuOptions && onOptionClick;
  return (
    <MUITableRow
      {...(onClick
        ? {
          onClick: () => props.onClick!(row),
          sx: { cursor: 'pointer' },
          hover: true,
        }
        : {})}
      tabIndex={-1}
    >
      {columns.map((column) => {
        return (
          <TableCell
            size="small"
            key={column.dataKey}
            align={column.numeric ? 'right' : 'left'}
            style={{
              width: `${column.width}%`,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              ...(column.cellTransform ? column.cellTransform(row) : {}),
            }}
          >
            <Tooltip key={column.dataKey}>
              <>{renderCell(row, column)}</>
            </Tooltip>
          </TableCell>
      )})}
      {onDeleteClick && (
        <TableCell size="small" align="right" sx={{ width: 60 }}>
          <Button
            icon={<DeleteTwoToneIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick(e, row);
            }}
          />
        </TableCell>
      )}
      {displayMenuOptions && (
        <TableCell size="small" align="right" sx={{ width: 60 }}>
          <MenuOptions
            item={row}
            options={row.menuOptions}
            onOptionClick={onOptionClick}
          />
        </TableCell>
      )}
    </MUITableRow>
  );
}

function Fallback({ length, fallback }: FallbackProps) {
  return (
    <tr>
      <td colSpan={length} align="center" style={{ padding: '35px 0' }}>
        <FallbackImage Image={{ src: fallback, width: 300, height: 200 }} />
      </td>
    </tr>
  );
}

export default function Table({ ...props }: TableProps) {
  const {
    sort, sortDirection, page, perPage,
  } = props.tableState;

  const isEmpty = !props.data?.length && !props.isLoading && !props.isError;
  const hasData = !!props.data?.length && !props.isLoading && !props.isError;

  const tableContainerStyles = {
    minHeight: isEmpty || props.isError ? 350 : 0,
    mb: 2,
  };

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer component={Paper} sx={tableContainerStyles}>
        <MUITable stickyHeader aria-labelledby="Table" sx={{ tableLayout: 'fixed' }}>
          <TableHead
            order={sortDirection}
            orderBy={sort}
            columns={props.columns}
            numberOfRows={props.data?.length}
            onOptionClick={props.onOptionClick}
            onRequestSort={props.onSort}
            onDeleteClick={props.onDeleteClick}
          />
          <TableBody>
            {props.isLoading && (
              <MUITableRow>
                <TableCell align="center" colSpan={props.columns.length}>
                  <CircularProgress />
                </TableCell>
              </MUITableRow>
            )}
            {props.isError && (<Fallback length={props.columns.length} fallback="errorCard" />)}
            {isEmpty && (<Fallback length={props.columns.length} fallback="empty" />)}
            {hasData && props.data.map((row, index) => (
              <TableRow
                key={index}
                row={row}
                columns={props.columns}
                onOptionClick={props.onOptionClick}
                onClick={props.onClick}
                onDeleteClick={props.onDeleteClick}
              />
            ))}
          </TableBody>
        </MUITable>
      </TableContainer>
      {props.onPaginate && (props.data.length > 0) && (
        <PaginationIndicator
          page={page}
          itemsPerPage={perPage}
          totalItems={props.totalItemCount || props.data.length}
          onPageChange={props.onPaginate}
          {...(props.onPerPageChange ? {
            onRowsPerPageChange: props.onPerPageChange,
          } : {})}
        />
      )}
    </Box>
  );
}
